'use client';
import { useCounter } from '@capsicum/common';
import { type CSSProperties, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import Cropper, { type Area, type CropperProps } from 'react-easy-crop';
import styled from 'styled-components';

import { RotateIcon, ZoomInIcon, ZoomOutIcon } from '../../icons';

export type CroppedImageShape = {
	height: number;
	image: string;
	mimeType: 'image/jpeg';
	width: number;
};

export type ImageCropperProps = {
	cropperProps: Partial<CropperProps>;
	height?: CSSProperties['height'];
	onCroppedImage: (arg1: CroppedImageShape) => void;
	testID: string;
};

const CROP_MIME_TYPE = 'image/jpeg';
const MAX_ROTATION = 360;
const ROTATION_STEP = 90;

const getCroppedImg = async (imgSrc: string, croppedAreaPixels: Area): Promise<CroppedImageShape> => {
	const { height, width, x, y } = croppedAreaPixels;

	return new Promise<HTMLImageElement>((resolve, reject) => {
		const img = new Image();

		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = imgSrc;
		// For avoiding CORS errors.
		img.crossOrigin = 'anonymous';
	}).then((img: HTMLImageElement) => {
		const tnCanvas = document.createElement('canvas');

		tnCanvas.width = width;
		tnCanvas.height = height;
		const ctx = tnCanvas.getContext('2d');

		if (!ctx) throw new Error('Canvas 2D context is not available.');

		ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

		const dataURL: string = tnCanvas.toDataURL(CROP_MIME_TYPE);

		const croppedImage: CroppedImageShape = {
			height: height,
			image: dataURL,
			mimeType: CROP_MIME_TYPE,
			width: width
		};

		return croppedImage;
	});
};

export const ImageCropper = ({ cropperProps, height = '350px', onCroppedImage, testID }: ImageCropperProps) => {
	const {
		count: zoom,
		decrement: decrementZoom,
		increment: incrementZoom
	} = useCounter({ defaultValue: 1, max: 3, min: 1, step: 0.3 });
	const {
		count: rotation,
		increment: incrementRotation,
		setCount: setRotation
	} = useCounter({ defaultValue: 0, max: MAX_ROTATION, min: 0, step: ROTATION_STEP });

	const [crop, setCrop] = useState({ x: 0, y: 0 });

	const { image } = cropperProps;

	const handleCropComplete = async (_: Area, croppedAreaPixels: Area) => {
		if (!image) return;

		const croppedImg = await getCroppedImg(image, croppedAreaPixels);

		onCroppedImage(croppedImg);
	};

	const handleRotation = () => {
		const firstRotation = ROTATION_STEP;

		if (rotation === MAX_ROTATION) {
			setRotation(firstRotation);

			return;
		}

		incrementRotation();
	};

	if (!image) return null;

	return (
		<S.Container $height={height} data-testid={`${testID}-container`}>
			<S.CropperContainer>
				<Cropper
					crop={crop}
					rotation={rotation}
					zoom={zoom}
					onCropChange={setCrop}
					onCropComplete={handleCropComplete}
					{...cropperProps}
				/>
			</S.CropperContainer>
			<S.ActionsContainer>
				<S.ActionArea data-testid={`${testID}-zoom-out`} onClick={() => decrementZoom()}>
					<S.StyledZoomOutIcon />
				</S.ActionArea>
				<S.ActionArea data-testid={`${testID}-zoom-in`} onClick={() => incrementZoom()}>
					<S.StyledZoomInIcon />
				</S.ActionArea>
				<S.ActionArea data-testid={`${testID}-rotate`} onClick={handleRotation}>
					<S.StyledRotateIcon />
				</S.ActionArea>
			</S.ActionsContainer>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div<{ $height: ImageCropperProps['height'] }>`
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
		height: ${({ $height }) => $height};
		position: relative;
	`;

	export const CropperContainer = styled.div`
		position: relative;
		height: 100%;
		width: 100%;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.subtle};
	`;

	export const ActionsContainer = styled.div`
		display: flex;
		align-items: center;
		justify-content: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		padding: ${({ theme }) => theme.spacings.xxxsmall};
		width: 100%;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.subtle};
	`;

	export const ActionArea = styled.div`
		padding: ${({ theme }) => theme.spacings.xxsmall};
		cursor: pointer;
	`;

	export const StyledRotateIcon = styled(RotateIcon)`
		width: ${({ theme }) => theme.sizing.medium};
		height: ${({ theme }) => theme.sizing.medium};
		color: ${({ theme }) => theme.colors.icons.main.enabled};
	`;
	export const StyledZoomInIcon = styled(ZoomInIcon)`
		width: ${({ theme }) => theme.sizing.medium};
		height: ${({ theme }) => theme.sizing.medium};
		color: ${({ theme }) => theme.colors.icons.main.enabled};
	`;
	export const StyledZoomOutIcon = styled(ZoomOutIcon)`
		width: ${({ theme }) => theme.sizing.medium};
		height: ${({ theme }) => theme.sizing.medium};
		color: ${({ theme }) => theme.colors.icons.main.enabled};
	`;
}
